import { useEffect, useRef, type ReactNode } from "react";

/**
 * L'ispettore contesto quando non ha una colonna sua: foglio dal basso su
 * telefono, cassetto da destra su portatile (Davide 2026-08-24).
 *
 * Unico per entrambi: il markup del foglio mobile esisteva già dentro
 * MessaggiPage e duplicarlo per il portatile avrebbe voluto dire due modali da
 * tenere allineate su fuoco, Esc e chiusura.
 */
export function ContestoOverlay({
  variante,
  onClose,
  children,
}: {
  variante: "basso" | "destra";
  onClose: () => void;
  children: ReactNode;
}) {
  const chiudiRef = useRef<HTMLButtonElement>(null);
  // Il fuoco torna dove stava: senza, chiudendo il cassetto da tastiera si
  // riparte dall'inizio della pagina.
  const provenienzaRef = useRef<Element | null>(null);

  useEffect(() => {
    provenienzaRef.current = document.activeElement;
    const onKey = (event: KeyboardEvent): void => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    chiudiRef.current?.focus();
    return () => {
      window.removeEventListener("keydown", onKey);
      const provenienza = provenienzaRef.current;
      if (provenienza instanceof HTMLElement && document.contains(provenienza)) {
        provenienza.focus();
      }
    };
  }, [onClose]);

  const dalBasso = variante === "basso";

  // Modale solo il foglio del telefono, che copre lo schermo e toglie davvero
  // la chat di mezzo. Il cassetto del portatile e' informazione di contorno
  // accanto alla conversazione: dichiararlo modale direbbe il falso a chi usa
  // il lettore di schermo, e sovrapporrebbe due `dialog` quando da qui si apre
  // il dialogo del reso.
  return (
    <div
      className={["fixed inset-0 z-[9992] flex", dalBasso ? "flex-col" : "flex-row"].join(" ")}
      {...(dalBasso
        ? { role: "dialog" as const, "aria-modal": true }
        : { role: "complementary" as const })}
      aria-label="Contesto ordine"
    >
      <button
        type="button"
        aria-label="Chiudi contesto"
        onClick={onClose}
        className="flex-1 bg-black/40"
      />
      <div
        className={[
          "flex min-h-0 flex-col overflow-y-auto border-line bg-card",
          dalBasso
            ? "max-h-[85dvh] rounded-t-lg border-t pb-[env(safe-area-inset-bottom)]"
            : "h-full w-[min(360px,85vw)] border-l shadow-popover",
        ].join(" ")}
      >
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-line bg-card px-4 py-2.5">
          <span className="font-serif text-[15px] font-medium text-ink">Contesto ordine</span>
          <button
            ref={chiudiRef}
            type="button"
            onClick={onClose}
            aria-label="Chiudi"
            className="flex h-9 w-9 items-center justify-center rounded-sm text-ink-3 hover:bg-paper hover:text-ink"
          >
            <svg
              className="h-4 w-4 fill-none stroke-current stroke-[1.8] [stroke-linecap:round]"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
